import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Alert,
  Badge,
  LightModeSemanticColors,
  TertiaryButton,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'
import { FaTriangleExclamationSolid } from '@enechain/ecloud-icons'

import { Modal } from '~/components/common/Modal'
import { iconSize } from '~/constants/iconSize'
import { submittedKomaNumbers } from '~/models/planSubmission/gcExcludedKoma'

import { useFormatKomaRanges } from './useFormatKomaRanges'

type Props = {
  /** 除外コマ番号 (1-origin, 1..48, 昇順)。空配列 (または未除外) なら何も描画しない。 */
  excludedKomaNumbers: number[]
}

/**
 * GC 経過による一部除外を示す「一部除外」warning chip (gc-excluded-koma-visibility
 * design AC-PLC-037-2)。 一覧行 (`ProcurementPlanTable`) と詳細ヘッダ (`DetailHeader`)
 * の両方から共有する (Verification Strategy F2: component 契約を 1 箇所で固定)。
 *
 * - `excludedKomaNumbers` が空なら何も描画しない (決定 5: 空表示条件は backend が
 *   判定して返す値をそのまま反映する。 frontend で追加判定はしない)
 * - click で詳細 modal を開く。 行 click 等への伝播は `stopPropagation` で止める
 *   (`PlanSubmitErrorIndicator` と同じ pattern)
 * - 送信コマ範囲は「全 48 − 除外集合」の補集合として frontend が導出する
 *   (invariant は Design Note Contract/Data。 backend field は持たない)
 * - 垂直位置合わせ (design `.pr-gc-mark` の `vertical-align: middle` 相当) は
 *   本 component 自身が閉じる。 呼び出し側 (一覧行の隣接 Badge / 詳細ヘッダの
 *   status item) で wrapper span を足す必要はない (2 surface で揃える)
 */
export const GcExcludedKomaMark = ({
  excludedKomaNumbers,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const formatKomaRanges = useFormatKomaRanges()

  if (excludedKomaNumbers.length === 0) {
    return null
  }

  const excludedText = formatKomaRanges(excludedKomaNumbers)
  const submittedText = formatKomaRanges(
    submittedKomaNumbers(excludedKomaNumbers),
  )

  return (
    <>
      <Tooltip content={t('page:planSubmission.gcExcludedKoma.chipTooltip')}>
        {/* [DS差異] DS Badge は clickable/polymorphic な as prop を公開していない
            (frontend/node_modules 実測)。design `.pr-gc-mark` も生の <button> の
            ため、native button で Badge をラップしクリック可能にする。style
            reset (background/border/padding) はこの button 自身の見た目を
            透明化して内側の Badge だけを見せるためのもので、DS recipe を
            打ち消す意図ではない。 */}
        <button
          onClick={(e) => {
            // 一覧行等の onRowClick が発火して画面遷移するのを止める。
            e.stopPropagation()
            setIsOpen(true)
          }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'inline-flex',
            marginLeft: '4px',
            padding: 0,
            verticalAlign: 'middle',
          }}
          type="button"
        >
          <Badge status="warning">
            <span
              style={{
                alignItems: 'center',
                display: 'inline-flex',
                gap: '2px',
              }}
            >
              <FaTriangleExclamationSolid
                boxSize={iconSize.badgeIcon}
                color={LightModeSemanticColors.Text.warningHigh.value}
                aria-hidden="true"
              />
              {t('page:planSubmission.gcExcludedKoma.chipLabel')}
            </span>
          </Badge>
        </button>
      </Tooltip>
      {/* Modal は React portal で描画されるが、portal 内で発生した click も
          React の合成イベントは親ツリー (この component を含む祖先、例えば
          一覧行の onRowClick) へ伝播する。footer の閉じるボタン / ヘッダの
          × / 本文操作いずれも一覧行クリックへ伝播させないよう、Modal 全体を
          stopPropagation する wrapper で包む (トリガー button 側の
          stopPropagation だけでは開いた後の modal 内操作を止められない)。 */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- ユーザー操作を捕捉する要素ではなく、Modal (portal) の click 合成イベントが祖先へ伝播するのを止めるだけの非表示 wrapper */}
      <span onClick={(e) => e.stopPropagation()}>
        <Modal
          footer={
            <TertiaryButton onClick={() => setIsOpen(false)}>
              {t('common:action.close')}
            </TertiaryButton>
          }
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          size="sm"
          title={t('page:planSubmission.gcExcludedKoma.modalTitle')}
        >
          <Alert
            marginBottom={3}
            status="warning"
            title={t('page:planSubmission.gcExcludedKoma.modalAlertTitle')}
          />
          {/*
           * 定義リスト (除外コマ / 送信コマ)。DS に定義リスト component が無く、
           * design (`design/plan/procurement.html` の `#pr-gc-modal`) も生の
           * <dl>/<dt>/<dd> を使っているため同じ semantics を採用する
           * (label → value の対応を DOM 構造で表現し、test から dt の
           * nextElementSibling で dd を引けるようにする)。
           */}
          <dl
            style={{
              display: 'grid',
              gap: '8px 12px',
              gridTemplateColumns: '7.5rem 1fr',
              margin: '12px 0 0',
            }}
          >
            <Text as="dt" color="Text.highEmphasis" fontWeight="semibold">
              {t('page:planSubmission.gcExcludedKoma.excludedLabel')}
            </Text>
            <Text as="dd" margin={0} color="Text.highEmphasis">
              {excludedText}
            </Text>
            <Text as="dt" color="Text.highEmphasis" fontWeight="semibold">
              {t('page:planSubmission.gcExcludedKoma.submittedLabel')}
            </Text>
            <Text as="dd" margin={0} color="Text.highEmphasis">
              {submittedText}
            </Text>
          </dl>
          <Text marginTop={3} color="Text.midEmphasis" fontSize="sm">
            {t('page:planSubmission.gcExcludedKoma.modalDescription')}
          </Text>
        </Modal>
      </span>
    </>
  )
}
